import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Fade,
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  CircularProgress
} from "@material-ui/core";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { Api } from "../api/client";
import BranchSelect from "../Components/BranchSelect";
import DateRangeBar from "../Components/DateRangeBar";
import KpiCards from "../Components/KpiCards";
import ExportButtons from "../Components/ExportButtons";
import DataTable from "../Components/DataTable";
import { money, num, pct } from "../utils/format";
import { useReactToPrint } from "react-to-print";
import HeatmapTableColor from "../Components/HeatmapTableColor";

import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend
} from "chart.js";

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend
);

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/* =========================
   Helpers de normalización
   (para que tus APIs puedan enviar arrays crudos)
   ========================= */
function normalizeSummary(k) {
  // tu summary viene: [ { totalVentas:"..", pedidos:"..", ticketPromedio:"..", clientesNuevos:".." ... } ]
  const row = Array.isArray(k) ? (k[0] || {}) : (k || {});
  return {
    totalVentas: Number(row.totalVentas || 0),
    pedidos: Number(row.pedidos || 0),
    ticketPromedio: Number(row.ticketPromedio || 0),
    clientesNuevos: Number(row.clientesNuevos || 0),
    cancelaciones: Number(row.cancelaciones || 0),
    devoluciones: Number(row.devoluciones || 0)
  };
}

function normalizeByHour(h) {
  // API te devuelve array: [{hora:"8", cantidad:"6"}, ...]
  if (Array.isArray(h)) {
    const labels = h.map(x => String(x.hora).padStart(2, "0"));
    const values = h.map(x => Number(x.cantidad || 0));
    return { labels, values };
  }
  // fallback si ya viene {labels,values}
  const labels = Array.isArray(h?.labels) ? h.labels : [];
  const values = Array.isArray(h?.values) ? h.values : [];
  return { labels, values };
}

function normalizeByDay(d) {
  // API te devuelve array: [{fecha:"2026-01-22", ventas:"799.2"}, ...]
  if (Array.isArray(d)) {
    const labels = d.map(x => x.fecha);
    const values = d.map(x => Number(x.ventas || 0));
    return { labels, values };
  }
  const labels = Array.isArray(d?.labels) ? d.labels : [];
  const values = Array.isArray(d?.values) ? d.values : [];
  return { labels, values };
}

/* =========================
   SECCIONES (UI) - NO hacen fetch
   ========================= */
function FiltersBar({ branches, branchId, setBranchId, range, setRange }) {
  return (
    <Box mt={2} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={12}>
      <Box display="flex" alignItems="center" gridGap={12} flexWrap="wrap">
        <BranchSelect value={branchId} options={branches} onChange={setBranchId} />
        <DateRangeBar from={range.from} to={range.to} onChange={setRange} />
      </Box>
    </Box>
  );
}

function SectionSales({ params, byDay, byHour, chartSalesByDay, chartSalesByHour, onPrint }) {
  return (
    <Box mt={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper style={{ padding: 16, borderRadius: 14 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={10}>
              <Typography variant="h6" style={{ fontWeight: 900 }}>Ventas por período (día)</Typography>
              <ExportButtons
                filename={`ventas_por_dia_${params.from}_a_${params.to}.csv`}
                rows={(byDay?.labels || []).map((l, i) => ({ fecha: l, ventas: (byDay.values || [])[i] }))}
                onPrint={onPrint}
              />
            </Box>
            <Divider style={{ margin: "12px 0" }} />
            {chartSalesByDay ? <Line data={chartSalesByDay.data} options={chartSalesByDay.options} /> : null}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper style={{ padding: 16, borderRadius: 14 }}>
            <Typography variant="h6" style={{ fontWeight: 900 }}>Comportamiento por horas</Typography>
            <Divider style={{ margin: "12px 0" }} />
            {chartSalesByHour ? <Bar data={chartSalesByHour.data} options={chartSalesByHour.options} /> : null}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

function SectionProducts({ params, products, columnsProducts, chartProductShare, onPrint }) {
  return (
    <Box mt={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper style={{ padding: 16, borderRadius: 14 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={10}>
              <Typography variant="h6" style={{ fontWeight: 900 }}>Venta por productos</Typography>
              <ExportButtons
                filename={`productos_${params.from}_a_${params.to}.csv`}
                rows={products}
                onPrint={onPrint}
              />
            </Box>
            <Divider style={{ margin: "12px 0" }} />
            <DataTable columns={columnsProducts} rows={products} />
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper style={{ padding: 16, borderRadius: 14 }}>
            <Typography variant="h6" style={{ fontWeight: 900 }}>Participación en ventas (Top 7)</Typography>
            <Divider style={{ margin: "12px 0" }} />
            {chartProductShare ? <Doughnut data={chartProductShare.data} options={chartProductShare.options} /> : null}

            <Box mt={2} style={{ fontSize: 12, opacity: .8 }}>
              <b>Insight rápido:</b> usa “Baja rotación” y “Extremo” para detectar productos dormidos vs. picos.
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

function SectionCustomers({ params, customers, ordersByCustomer, columnsTopClientes, columnsHistorialCliente, onPrint }) {
  return (
    <Box mt={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper style={{ padding: 16, borderRadius: 14 }}>
            <Typography variant="h6" style={{ fontWeight: 900 }}>Clientes frecuentes y nuevos</Typography>
            <Divider style={{ margin: "12px 0" }} />
            {customers ? (
              <Box display="flex" gridGap={14} flexWrap="wrap" style={{ fontSize: 13 }}>
                <div><b>Frecuentes:</b> {num(customers.frecuentes)}</div>
                <div><b>Nuevos:</b> {num(customers.nuevos)}</div>
                <div><b>Creados sin ventas:</b> {num(customers.creadosSinVentas)}</div>
              </Box>
            ) : null}

            <Box mt={2}>
              <DataTable columns={columnsTopClientes} rows={customers ? customers.topFrecuentes : []} maxHeight={320} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper style={{ padding: 16, borderRadius: 14 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={10}>
              <Typography variant="h6" style={{ fontWeight: 900 }}>Historial de pedidos por cliente</Typography>
              <ExportButtons
                filename={`historial_clientes_${params.from}_a_${params.to}.csv`}
                rows={ordersByCustomer}
                onPrint={onPrint}
              />
            </Box>
            <Divider style={{ margin: "12px 0" }} />
            <DataTable columns={columnsHistorialCliente} rows={ordersByCustomer} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

function SectionAvgTicket({ avgTicket }) {
  
  if (!avgTicket) return null;
  console.log(avgTicket.ticketPromedio)
  return (
    <Paper style={{ padding: 16, borderRadius: 14 }}>
      <Typography variant="h6" style={{ fontWeight: 900 }}>Ticket promedio</Typography>
      <Divider style={{ margin: "12px 0" }} />
      <Box style={{ fontSize: 13, opacity: .8 }}>
        <b>Ticket promedio del período:</b> {money(avgTicket.ticketPromedio)}
      </Box>
      <Box mt={2}>
        <Line
          data={{
            labels: (avgTicket.serie || []).map(x => x.label),
            datasets: [{
              label: "Ticket promedio",
              data: (avgTicket.serie || []).map(x => Number(x.value))
            }]
          }}
          options={{ responsive: true, plugins: { legend: { display: true } } }}
        />
      </Box>
    </Paper>
  );
}

function SectionHeatmap({ heatmap }) {
  if (!heatmap) return null;
  return (
    <Paper style={{ padding: 16, borderRadius: 14 }}>
      <Typography variant="h6" style={{ fontWeight: 900 }}>Horas pico (mapa de calor)</Typography>
      <Divider style={{ margin: "12px 0" }} />
      <HeatmapTableColor days={heatmap.days} hours={heatmap.hours} matrix={heatmap.matrix} />
    </Paper>
  );
}

function SectionCancellations({ params, cancels, columnsCancel, onPrint }) {
  return (
    <Box mt={2}>
      <Paper style={{ padding: 16, borderRadius: 14 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={10}>
          <Typography variant="h6" style={{ fontWeight: 900 }}>
            Cancelaciones, devoluciones y cambio de sucursal
          </Typography>
          <ExportButtons
            filename={`cancelaciones_${params.from}_a_${params.to}.csv`}
            rows={cancels ? cancels.detalle : []}
            onPrint={onPrint}
          />
        </Box>

        <Divider style={{ margin: "12px 0" }} />

        {cancels ? (
          <Box display="flex" gridGap={16} flexWrap="wrap" style={{ fontSize: 13 }}>
            <div><b>Cancelaciones:</b> {num(cancels.resumen.cancelaciones)}</div>
            <div><b>Devoluciones:</b> {num(cancels.resumen.devoluciones)}</div>
            <div><b>Cambios de sucursal:</b> {num(cancels.resumen.cambiosSucursal)}</div>
          </Box>
        ) : null}

        <Box mt={2}>
          <DataTable columns={columnsCancel} rows={cancels ? cancels.detalle : []} />
        </Box>
      </Paper>
    </Box>
  );
}

/* =========================
   CONTAINER (FETCH + ESTADO)
   ========================= */
export default function DashboardGerencial() {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [range, setRange] = useState({ from: daysAgoISO(7), to: todayISO() });

  const [loading, setLoading] = useState(false);

  const [kpi, setKpi] = useState(null);
  const [byHour, setByHour] = useState(null);
  const [byDay, setByDay] = useState(null);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState(null);
  const [ordersByCustomer, setOrdersByCustomer] = useState([]);
  const [avgTicket, setAvgTicket] = useState(null);
  const [cancels, setCancels] = useState(null);
  const [heatmap, setHeatmap] = useState(null);

  const printRef = useRef(null);
  const onPrint = useReactToPrint({ content: () => printRef.current });

  const params = useMemo(() => ({
    from: range.from,
    to: range.to,
    branch: branchId // "" = todas
  }), [range, branchId]);

  useEffect(() => {
    Api.branches()
      .then(res => setBranches(res.data))
      .catch(() => setBranches([{ id: "", nombre: "Todas las sucursales" }]));
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [k, h, d, p, c, obc, at, ca, hm] = await Promise.all([
        Api.salesSummary(params),
        Api.salesByHour(params),
        Api.salesByDay(params),
        Api.productsRanking(params),
        Api.customersStats(params),
        Api.ordersByCustomer(params),
        Api.avgTicket(params),
        Api.cancellations(params),
        Api.heatmap(params)
      ]);

      // 👇 normalizamos aquí (CLAVE)
      const kNorm = normalizeSummary(k);
      const byHourNorm = normalizeByHour(h);
      const byDayNorm = normalizeByDay(d);

      setKpi(kNorm);
      setByHour(byHourNorm);
      setByDay(byDayNorm);
      setProducts(Array.isArray(p) ? p : (p?.data || []));
      setCustomers(c);
      setOrdersByCustomer(Array.isArray(obc) ? obc : (obc?.data || []));
      setAvgTicket(at);
      setCancels(ca);
      setHeatmap(hm);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, [params.from, params.to, params.branch]);

  // Charts
  const chartSalesByDay = useMemo(() => {
    if (!byDay) return null;
    const labels = Array.isArray(byDay.labels) ? byDay.labels : [];
    const values = Array.isArray(byDay.values) ? byDay.values : [];
    return {
      data: {
        labels: [...labels],
        datasets: [{ label: "Ventas (USD)", data: [...values] }]
      },
      options: { responsive: true, plugins: { legend: { display: true } } }
    };
  }, [byDay]);

  const chartSalesByHour = useMemo(() => {
    if (!byHour) return null;
    const labels = Array.isArray(byHour.labels) ? byHour.labels : [];
    const values = Array.isArray(byHour.values) ? byHour.values : [];
    return {
      data: {
        labels: [...labels],
        datasets: [{ label: "Pedidos por hora", data: [...values] }]
      },
      options: {
        responsive: true,
        plugins: { legend: { display: true } },
        scales: { x: { ticks: { maxRotation: 0, autoSkip: true } } }
      }
    };
  }, [byHour]);

  const chartProductShare = useMemo(() => {
    if (!products || !products.length) return null;
    const top = products.slice(0, 7);
    return {
      data: {
        labels: top.map(x => x.producto),
        datasets: [{
          label: "Participación % (USD)",
          data: top.map(x => Number(x.participacion))
        }]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { position: "bottom" },
          tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${Number(ctx.raw).toFixed(2)}%` } }
        }
      }
    };
  }, [products]);

  const columnsProducts = [
    { key: "producto", label: "Producto" },
    { key: "categoria", label: "Categoría" },
    { key: "cantidad", label: "Cantidad", render: (r) => num(r.cantidad) },
    { key: "ingreso", label: "Ingresos", render: (r) => money(r.ingreso) },
    { key: "participacion", label: "Participación", render: (r) => pct(r.participacion) },
    { key: "bajaRotacion", label: "Baja rotación", render: (r) => r.bajaRotacion ? "Sí" : "No" },
    { key: "extremo", label: "Extremo", render: (r) => r.extremo ? "Sí" : "No" }
  ];

  const columnsTopClientes = [
    { key: "cliente", label: "Cliente" },
    { key: "pedidos", label: "Pedidos", render: (r) => num(r.pedidos) },
    { key: "gasto", label: "Gasto", render: (r) => money(r.gasto) },
    { key: "ultimaCompra", label: "Última compra" }
  ];

  const columnsHistorialCliente = [
    { key: "cliente", label: "Cliente" },
    { key: "pedidos", label: "Pedidos", render: (r) => num(r.pedidos) },
    { key: "frecuenciaDias", label: "Frecuencia (días)" },
    { key: "preferencia", label: "Preferencia" },
    { key: "ticketProm", label: "Ticket prom", render: (r) => money(r.ticketProm) }
  ];

  const columnsCancel = [
    { key: "fecha", label: "Fecha" },
    { key: "tipo", label: "Tipo" },
    { key: "sucursalOrigen", label: "Origen" },
    { key: "sucursalDestino", label: "Destino" },
    { key: "monto", label: "Monto", render: (r) => money(r.monto) },
    { key: "motivo", label: "Motivo" }
  ];

  return (
    <>
      {/* Loading modal global */}
      <Backdrop
        open={loading}
        style={{
          zIndex: 2000,
          color: "#fff",
          backdropFilter: "blur(3px)"
        }}
      >
        <Fade in={loading}>
          <Paper style={{ padding: 32, borderRadius: 16, minWidth: 320, textAlign: "center" }}>
            <CircularProgress size={42} />
            <Typography variant="h6" style={{ marginTop: 16, fontWeight: 700 }}>
              Generando reporte…
            </Typography>
            <Typography variant="body2" style={{ marginTop: 6, opacity: 0.75 }}>
              Analizando ventas, clientes y pedidos
            </Typography>
          </Paper>
        </Fade>
      </Backdrop>

      <Box p={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={12}>
          <Box>
            <Typography variant="h5" style={{ fontWeight: 900 }}>Dashboard Gerencial (Call Center)</Typography>
            <Typography variant="body2" style={{ opacity: .75 }}>
              Reportería por sucursal o consolidado: ventas, productos, clientes, ticket promedio, cancelaciones y horas pico.
            </Typography>
          </Box>
        </Box>

        <FiltersBar
          branches={branches}
          branchId={branchId}
          setBranchId={setBranchId}
          range={range}
          setRange={setRange}
        />

        {/* Render solo cuando termina de cargar */}
        {!loading && (
          <div ref={printRef}>
            {/* KPI */}
            <KpiCards kpi={kpi} />

            {/* Ventas */}
            <SectionSales
              params={params}
              byDay={byDay}
              byHour={byHour}
              chartSalesByDay={chartSalesByDay}
              chartSalesByHour={chartSalesByHour}
              onPrint={onPrint}
            />

            {/* Productos */}
            <SectionProducts
              params={params}
              products={products}
              columnsProducts={columnsProducts}
              chartProductShare={chartProductShare}
              onPrint={onPrint}
            />

            {/* Clientes */}
            <SectionCustomers
              params={params}
              customers={customers}
              ordersByCustomer={ordersByCustomer}
              columnsTopClientes={columnsTopClientes}
              columnsHistorialCliente={columnsHistorialCliente}
              onPrint={onPrint}
            />

            {/* Ticket + Heatmap en 2 columnas */}
            <Box mt={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <SectionAvgTicket avgTicket={avgTicket} />
                </Grid>
                <Grid item xs={12} md={7}>
                  <SectionHeatmap heatmap={heatmap} />
                </Grid>
              </Grid>
            </Box>

            {/* Cancelaciones */}
            <SectionCancellations
              params={params}
              cancels={cancels}
              columnsCancel={columnsCancel}
              onPrint={onPrint}
            />
          </div>
        )}
      </Box>
    </>
  );
}